<template>
    <form @submit.prevent="submit()" class="batch-action-form">
        <section class="section-wrapper container-fluid px-0 page-header">
            <h3 class="page-title">
                <slot name="title"></slot>
            </h3>

            <div class="adminbar-wrapper">
                <slot name="page-actions" :form-valid="isFormValid" :finished="finished"></slot>
            </div>

            <hr>
        </section>

        <section class="page-content">
            <div class="row" v-if="!finished">
                <div class="col-md-5 col-lg-4 col-xl-3">
                    <div class="card items-column">
                        <div class="card-header mx-3 px-0 py-3">
                            <div class="title">
                                <slot name="items-title" :count="Object.values(itemsSelected).filter(v => v).length" :total="items.length"></slot>
                            </div>
                        </div>
                        <div class="card-body p-0">
                            <ul class="list-items">
                                <li class="item" v-for="item in items" :class="{'inactive': itemsSelected[item.id] === false}">
                                    <div class="form-check">
                                        <input :id="'formCheck-'+item.id" class="form-check-input" type="checkbox" :value="item.id" v-model="itemsSelected[item.id]" />
                                        <label class="form-check-label" :for="'formCheck-'+item.id">
                                            <slot name="item-template" :item="item"></slot>
                                        </label>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-md-7 col-lg-8 col-xl-9">
                    <div class="card">
                        <div class="card-header mx-3 px-0 py-3">
                            <div class="title"><i class="fas fa-align-justify mr-2"></i><span>{{ trans('title') }}</span></div>
                        </div>
                        <div class="card-body bulk-action-form">
                            <div class="table-responsive" ref="definedParameters">
                                <table class="table table--admin-listing">
                                    <thead>
                                        <tr>
                                            <th class="text-right pr-3 bg-light" style="width: 42%">{{ trans('field') }}</th>
                                            <th class="bg-light">{{ trans('value') }}</th>
                                            <th class="bg-light" style="width: 40px;"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="param in definedParameters" :ref="'definedParameterRow_'+param.name">
                                            <td class="text-right pr-3">
                                                <label class="pt-2 text-dark" for="title">{{ param.label }}</label>
                                            </td>
                                            <td class="px-0">
                                                <div class="d-flex align-items-center" :class="{'field-validation-error': isParameterInvalid(param.name) }">
                                                    <div style="width: 250px;">
                                                        <template v-if="param.format === 'amount'">
                                                            <input-amount :key="'definedField_'+param.name"
                                                                :name="param.name"
                                                                :operators="operators"
                                                                v-model="definedParametersValues[param.name]">
                                                            </input-amount>
                                                        </template>
                                                        <template v-else-if="param.format === 'choice'">
                                                            <div class="input-group" :class="{'is-invalid': isParameterInvalid(param.name) }">
                                                                <div class="input-group-addon"><i class="fa fa-ballot-check"></i></div>
                                                                <select :key="'definedField_'+param.name"
                                                                    class="form-control form-select"
                                                                    v-model="definedParametersValues[param.name]">
                                                                        <option v-for="opt in param.options" :value="opt.value">{{ opt.label }}</option>
                                                                </select>
                                                            </div>
                                                        </template>
                                                        <template v-else-if="param.format === 'date'">
                                                            <div class="input-group" :class="{'is-invalid': isParameterInvalid(param.name) }">
                                                                <div class="input-group-addon"><i class="fa fa-calendar-day"></i></div>
                                                                <datetime class="flatpickr"
                                                                    :config="datetimePickerConfig"
                                                                    v-model="definedParametersValues[param.name]">
                                                                </datetime>
                                                            </div>
                                                        </template>
                                                        <template v-else>
                                                            <div class="input-group" :class="{'is-invalid': isParameterInvalid(param.name) }">
                                                                <input type="text" class="form-control"
                                                                    v-model="definedParametersValues[param.name]">
                                                            </div>
                                                        </template>
                                                    </div>

                                                    <span class="validation-indicator">
                                                        <i class="fas fa-exclamation-circle text-danger" v-b-tooltip.hover v-b-tooltip.hover.right :title="trans('err-field-not-valid')"></i>
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="text-right">
                                                <button class="btn btn-light" type="button" v-on:click="removeParam(param)">
                                                    <i class="far fa-trash-alt text-danger"></i>
                                                </button>
                                            </td>
                                        </tr>

                                        <template v-if="!definedParameters.length">
                                            <tr>
                                                <td colspan="3" class="text-muted text-center py-3">{{ trans('no_defined_field') }}</td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                            </div>

                            <hr>

                            <table class="w-100">
                                <tr>
                                    <td class="text-muted text-right pr-3" style="width: 42%;">{{ trans('add_field') }}</td>
                                    <td class="px-0">
                                        <b-form-select class="mr-3" style="width: 250px" v-model="newParamDropdownValue">
                                            <optgroup v-for="group in availableGroupsAndOptionsForDropdown" :label="group.label">
                                                <option v-for="param in group.parameters" :value="param.name">{{ param.label }}</option>
                                            </optgroup>
                                        </b-form-select>
                                        <button class="btn btn-success btn-sm" type="button" v-on:click="addParam()" :disabled='!newParamDropdownValue'>
                                            <i class="fal fa-plus fa-fw"></i>
                                        </button>
                                    </td>
                                </tr>
                            </table>

                            <hr />

                            <div class="basic-form-items">
                                <slot name="basic-form-items" :basicDataValues="basicDataValues" :datetimePickerConfig="datetimePickerConfig" :definedParametersValues="definedParametersValues"></slot>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="loading-box" v-show="loading && !finished">
                <div class="loading-inner">
                    <h2 class="mb-5 title">{{ trans('saving_data') }}&hellip;</h2>
                    <i class="fa fa-spin fa-circle-notch text-secondary"></i>
                </div>
            </div>

            <div class="finished-pane" v-if="finished">
                <div class="card page-card p-4">
                    <template v-if="finishedResult.status === true">
                        <h2 class="title mb-5 text-success"><i class="fal fa-check-double mr-2"></i> {{ trans('finished_ok') }}</h2>
                        <p class="text-center">{{ trans('success_msg') }}</p>
                    </template>
                    <template v-else-if="finishedResult.status === false && finishedResult.errors">
                        <h2 class="title mb-5 text-warning"><i class="fal fa-exclamation-triangle mr-2"></i> {{ trans('processing_failed') }}</h2>
                        <table class="mb-4">
                            <tr><td>{{ trans('success_count') }}:</td> <td><b>{{ finishedResult.success }}</b></td></tr>
                            <tr><td>{{ trans('errors_count') }}:</td> <td><b class="text-danger">{{ finishedResult.failed }}</b></td></tr>
                        </table>
                        <p><b>{{ trans('not_processed_for_these_items') }}:</b></p>
                        <ul class="pl-3">
                            <li v-for="err in finishedResult.errors">{{ err }}</li>
                        </ul>
                    </template>
                    <template v-else>
                        <h2 class="title mb-5 text-danger"><i class="fal fa-times-hexagon mr-2"></i> {{ trans('processing_failed') }}</h2>
                        <p class="text-center font-weight-bold">{{ trans('problem_contact_support') }}</p>
                    </template>
                </div>
            </div>

            <modal
                name="submit-confirm-modal"
                class="modal--translation modal--hide-on-loading"
                v-cloak
                height="auto"
                :scrollable="true"
                :adaptive="true"
                :pivot-y="0.25"
            >
                <div class="modal-header p-0">
                    <button @click="closeSubmmitModal()" type="button" class="close close-modal mr-2"><i class="fal fa-fw fa-times"></i></button>
                    <h5 class="modal-title m-0 mb-2"><i class="fa fa-fw fa-question mr-1"></i> {{ trans('modal_submit_title') }}</h5>
                </div>

                <div class="modal-body py-5">
                    <h4 class="text-danger text-center p-0 m-0">{{ trans('modal_submit_confirm') }}</h4>
                </div>

                <div class="modal-footer px-0 justify-content-center">
                    <b-button type="button" variant="primary" @click="send()" :class="{'disabled': loading }"><i class="fa fa-check mr-2"></i> {{ trans('submit') }}</b-button>
                    <b-button type="button" variant="light" @click="closeSubmmitModal()"><i class="fa fa-times mr-2 text-dark"></i> {{ trans('cancel') }}</b-button>
                </div>
            </modal>

        </section>
    </form>
</template>

<script>
    import TranslationsUtils from "../app-components/TranslationsUtils";
    import DatetimePickerConfig from "../app-components/DatetimePickerConfig";
    import InputAmountWithOperator from "./basic/InputAmountWithOperator.vue";
    import {nextTick} from "vue";

    Vue.component('input-amount', InputAmountWithOperator);

    export default {
        mixins: [TranslationsUtils, DatetimePickerConfig],
        props: {
            items: { type: Array },
            parameterGroups: { type: Array },
            basicDataDefaults: { type: Object },
            saveUrl: { type: String }
        },

        data() {
            return {
                operators: [],
                itemsSelected: this.items.reduce((acc, item) => {
                                    acc[item.id] = true;
                                    return acc;
                               }, {}),
                definedParameters: [],
                definedParametersValues: {},
                invalidParameters: [],
                newParamDropdownValue: null,
                basicDataValues: this.basicDataDefaults,
                loading: false,
                finished: false,
                finishedResult: {
                    status: true,
                    success: 20,
                    failed: 0,
                    errors: []
                }
            };
        },

        mounted() {
            this.operators = [ // must be loaded after mounted, because of translation mixin which not works in data() method, yet.
                { label: this.trans('op_equals'), icon: 'fa-equals', value: '=' },
                { label: this.trans('op_plus'), icon: 'fa-plus', value: '+' },
                { label: this.trans('op_minus'), icon: 'fa-minus', value: '-' }
            ];
        },

        methods: {
            addParam() {
                let param = this.getParamFromGroupsListByName(this.newParamDropdownValue);

                if(param) {
                    this.definedParameters.push(param);
                    this.definedParametersValues[param.name] = '';

                    if (!this.definedParametersValues[param.name] && param.format === 'amount') {
                        this.$set(this.definedParametersValues, param.name, {
                            op: this.operators[0].value || '=',
                            value: ''
                        });
                    }

                    this.newParamDropdownValue = null;

                    nextTick(() => {
                        this.$refs['definedParameterRow_'+param.name][0].querySelector('input,select').focus();
                    });
                }
            },

            removeParam(param) {
                this.$delete(this.definedParametersValues, param.name);

                let idx = this.definedParameters.indexOf(param);
                if(idx !== -1) {
                    this.definedParameters.splice(idx, 1);
                }
            },

            getParamFromGroupsListByName(name) {
                for(let x in this.parameterGroups) {
                    let found = this.parameterGroups[x].parameters.find(param => param.name === name);
                    if(found) {
                        return found;
                    }
                }

                return null;
            },

            submit() {
                this.loading = false;
                this.$modal.show('submit-confirm-modal');
            },

            closeSubmmitModal() {
                this.$modal.hide('submit-confirm-modal');
            },

            send() {
                this.loading = true;
                this.invalidParameters = [];

                axios.post(this.saveUrl, {
                    'items': this.itemsSelected,
                    'basic': this.basicDataValues,
                    'fields': this.definedParametersValues,
                })
                    .then((response) => {
                        try {
                            this.showResults(response.data);
                            this.closeSubmmitModal();
                            this.loading = false;
                        } catch (err) {
                            //throw err; // nech prejde do .catch()
                        }
                    })
                    .catch(error => {
                        if (error.response.status === 422) {
                            this.$notify({
                              type: 'error',
                              title: this.trans('error'),
                              text: this.trans('fields_validation_error')
                            });

                            if(error.response.data.errors) {
                                this.handleValidationErrorsList(error.response.data.errors);
                            }
                        } else {
                            this.$notify({
                              type: 'error',
                              title: this.trans('error'),
                              text: error.response.data.message ? error.response.data.message : this.trans('unknown_error')
                            });
                        }

                        this.closeSubmmitModal();
                        this.loading = false;
                    });
            },

            showResults(data) {
                this.finished = true;
                this.finishedResult = data;
            },

            handleValidationErrorsList(errors) {
                for(let x in errors) {
                    if(this.definedParameters.find(param => param.name === x)) {
                        this.invalidParameters.push(x);
                    }
                }
            },

            isParameterInvalid(name) {
                return this.invalidParameters.indexOf(name) !== -1;
            },

        },

        computed: {
            isFormValid() {
                return this.definedParameters.length > 0;
            },
            availableGroupsAndOptionsForDropdown: {
                get() {
                    return this.parameterGroups.map((group) => {
                        return {
                            ...group,
                            parameters: group.parameters.filter(param =>
                                !this.definedParameters.some(defined => defined.name === param.name)
                            )
                        };
                    });
                },
                set() {}
            },
        }
    }
</script>


<style lang="scss">
    .btn-primary:disabled {
        opacity: 0.5;
        border-color: #4273fa !important;
        background: #4273fa !important;
    }

    .batch-action-form {
        .card-header, .page-title {
            user-select: none;
        }

        section.page-header .adminbar-wrapper {
            right: 0;
        }

        .items-column {
            margin-bottom: 0 !important;

            .card-body {
                max-height: calc(100vh - 200px);
                min-height: 200px;
                overflow: auto;
            }
        }

        .list-items {
            list-style-type: none;
            padding: 0;

            .item {
                padding: 2px 0px;
                border-bottom: solid #eef1f3 1px;
                margin: 0 15px;
                user-select: none;

                .form-check {
                    padding: 6px 0px;
                    margin-bottom: 0;

                    .form-check-label {
                        margin-bottom: 0;
                    }
                }

                .label {
                    display: flex;
                    align-items: center;
                }

                &.inactive {
                    opacity: 0.7;
                }
            }
        }

        .bulk-action-form {
            .subtitle {
                font-size: 1rem;
                margin: 0 0 1.5rem 0;
                text-align: center;
            }
            .help-icon {
                font-size: 1rem;
                margin-left: 1rem;
            }

            tbody {
                .input-group-prepend {
                    .btn {
                        border-radius: 0px !important;
                    }

                    .fa-chevron-down {
                        color: #444444;
                        font-size: 0.6rem;
                        margin-left: 0.6rem;
                    }
                }
                .input-group-addon {
                    border-radius: 0px !important;
                }


                .validation-indicator {
                    display: none;
                }

                .field-validation-error {
                    .validation-indicator {
                        display: inline-block;
                        margin-left: 1.8rem;

                        i {
                            font-size: 1.2rem;
                        }
                    }
                }

                .input-group.is-invalid {
                    .form-control, .input-group-addon {
                        border: 1px solid #dc3545;
                    }
                    .input-group-addon:not(:last-child) {
                        border-right: 0px !important;
                    }
                }

            }
        }

        .basic-form-items {
            .input-group-addon {
                border-radius: 0px !important;
            }
        }

        .bulk-action-form tbody .input-group, .bulk-action-form tbody .form-select, .basic-form-items .form-control {
            max-width: 250px;
        }

        .loading-inner, .countdown-inner {
            background: white;
            padding: 40px;
            box-shadow: 0px 0px 4px 1px #EEE;
            border-radius: 6px;

            h2 { color: #555; }
        }

        .table--admin-listing {
            td:last-of-type {
                min-width: auto;
                display: table-cell;
                justify-content: initial;
            }
        }

        .finished-pane {
            margin: 2rem auto;
            max-width: 600px;
            font-size: 0.9rem;

            .title {
                font-size: 1.2rem;
            }

            table {
                width: 300px;

                td {
                    max-width: 50%;
                    line-height: 150%;
                }
            }

            ul li { margin-bottom: 0.3rem; }

        }

        .loading-box {
            position: absolute;
            top: 0;
            left: 0;
            background: rgba(255,255,255,0.6);
            z-index: 10;

            .loading-inner {
                text-align: center;
                z-index: 10;

                .title {
                    font-size: 1.3rem;
                    color: #4273FA;
                }
            }
        }
    }
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/BatchActionForm.vue